import { useMemo } from "react";
import { gql, Graph } from "~/Graph";

import { Company } from ".";

export namespace Settings {
  export type DriveAndTrackSettings = Graph.Generated.Company__Settings__Drive &
    Graph.Generated.Company__Settings__Track &
    Graph.Generated.Node;
  export namespace Fragment {
    export const document = () => gql`
      fragment TrackSettings on Company__Settings {
        trackSettings {
          ... on Company__Settings__Track {
            createWorkOrdersForDVIRs
          }
          ... on Error {
            message
          }
        }
      }

      fragment DriveSettings on Company__Settings {
        driveSettings {
          ... on Company__Settings__Drive {
            requireImagesForAllDVIRs
            requireNotesForAllDVIRs
            requireDriverToViewAndAcceptCompanyDefectList
            enableFacialRecognitionPairingSuggestions
          }
          ... on Error {
            message
          }
        }
      }
    `;
  }

  export type Query = Graph.Generated.SettingsQuery;

  export namespace Query {
    export type Variables = {
      readonly companyID: number;
    };

    export const document = () => gql`
      ${Fragment.document()}
      ${Graph.Errors.Fragment.document()}

      query Settings($companyID: ID!) {
        companies(id: $companyID) {
          ... on Company {
            id
            settings {
              ... on Company__Settings {
                ...TrackSettings
                ...DriveSettings
              }
              ... on Error {
                ...Error
              }
            }
          }
          ... on Error {
            ...Error
          }
        }
      }
    `;

    export const use = () => {
      const { data, loading, error: apolloError, refetch } = Graph.useQuery<
        Settings.Query,
        Variables
      >(document(), {
        variables: {
          companyID: parseInt(Company.use().company.id, 10),
        },
      });

      const [settings, graphError] = useMemo(() => {
        const decoded = data && decode(data);
        const isError = decoded && "message" in decoded;
        const isData = !!decoded && !isError;

        const parsedData: DriveAndTrackSettings | undefined = isData
          ? (decoded as DriveAndTrackSettings)
          : undefined;

        const parsedError = isError ? (decoded as Graph.Errors) : undefined;

        return [parsedData, parsedError];
      }, [data, apolloError]);

      return {
        settings,
        loading,
        refetch,
        apolloError,
        graphError,
      };
    };

    type plainSettingsResponse = {
      readonly companies: {
        readonly id: string;
        readonly settings: {
          readonly driveSettings: {
            readonly requireImagesForAllDVIRs: boolean;
            readonly requireNotesForAllDVIRs: boolean;
            readonly requireDriverToViewAndAcceptCompanyDefectList: boolean;
            readonly enableFacialRecognitionPairingSuggestions: boolean;
          };
          // Track settings stuff here if/when we need it from the graph
          readonly trackSettings: {
            readonly createWorkOrdersForDVIRs: boolean;
          };
        };
      };
    };

    export const decode = (
      query: Query
    ): DriveAndTrackSettings | Graph.Errors => {
      // Handle companies error
      if (query.companies && "message" in query.companies)
        return Graph.Errors.Fragment.decode(query.companies);

      // Handle companies.settings error
      if (query.companies?.settings && "message" in query.companies.settings)
        return Graph.Errors.Fragment.decode(query.companies.settings);

      const { companies } = query as plainSettingsResponse;

      const allSettings = companies?.settings;

      return {
        id: companies.id,
        requireImagesForAllDVIRs:
          allSettings.driveSettings.requireImagesForAllDVIRs,
        requireNotesForAllDVIRs:
          allSettings.driveSettings.requireNotesForAllDVIRs,
        requireDriverToViewAndAcceptCompanyDefectList:
          allSettings.driveSettings
            .requireDriverToViewAndAcceptCompanyDefectList,
        enableFacialRecognitionPairingSuggestions:
          allSettings.driveSettings
            .enableFacialRecognitionPairingSuggestions,
        createWorkOrdersForDVIRs:
          allSettings.trackSettings.createWorkOrdersForDVIRs,
      };
    };
  }

  export namespace Update {
    export type Variables = {
      readonly companyID?: number;
      readonly requireImages: boolean;
      readonly requireNotes: boolean;
      readonly requireDefectListReview: boolean;
      readonly enablePairingSuggestions: boolean;
    };

    export type VariablesWithoutCompany = Pick<
      Variables,
      "requireImages" | "requireNotes" | "requireDefectListReview" | "enablePairingSuggestions"
    >;

    export const document = () => gql`
      mutation UpdateCompanySettings(
        $companyID: ID!
        $requireImages: Boolean!
        $requireNotes: Boolean!
        $requireDefectListReview: Boolean!
        $enablePairingSuggestions: Boolean!
      ) {
        companySettingsUpdate(
          companyID: $companyID
          input: {
            requireImagesForAllDVIRs: $requireImages
            requireNotesForAllDVIRs: $requireNotes
            requireDriverToViewAndAcceptCompanyDefectList: $requireDefectListReview
            enableFacialRecognitionPairingSuggestions: $enablePairingSuggestions
          }
        ) {
          ... on Company__Settings__Drive {
            requireImagesForAllDVIRs
            requireNotesForAllDVIRs
            requireDriverToViewAndAcceptCompanyDefectList
            enableFacialRecognitionPairingSuggestions
          }

          ... on Error {
            message
          }
        }
      }
    `;

    export const use = ({
      requireImages,
      requireNotes,
      requireDefectListReview,
      enablePairingSuggestions,
    }: VariablesWithoutCompany) =>
      Graph.useMutation<Graph.Generated.SettingsQuery, Variables>(document(), {
        variables: {
          companyID: parseInt(Company.use().company.id, 10),
          requireImages,
          requireNotes,
          requireDefectListReview,
          enablePairingSuggestions,
        },
      });
  }
}
